// =============================================================================
// AXIS AFFINITY CHIPS — AXIS-P2 (docs/design/axis-lens-plan-2026-07-06.md §5:
// "Tags visible + correctable... show the axis chips (compact, one ramp,
// tokens-only); a remove affordance per chip... and a confirm affordance").
//
// Renders a corpus card's own axisAffinities[] as small chips — ONE ramp
// (var(--tl-accent)/var(--tl-accent-soft)), mirroring AxisLensCard's own
// register-chip idiom (a DIFFERENT concept: this reads the CARD's own tagged
// axes, not a posting's JD-weight read — never conflated in copy, same
// discipline as AxisLensService.ts's own role_axes/radarAxes reconciliation
// note). A model-sourced chip (source:'model') gets confirm + remove
// affordances; a user-sourced chip (source:'user', already terminal) gets
// ONLY remove (there is nothing left to "confirm" — it is already the user's
// own ruling) — reflecting AxisAffinityTaggerService's own terminality law
// directly in what the chip can DO, not just how it looks.
//
// SERVED DATA ONLY (no-frontend-bandaids): this component never computes or
// re-scores an affinity client-side — every chip is a direct render of a
// server-provided AxisAffinityRecord; the confirm/remove taps call the two
// new POST /corpus/axis-affinities/:subjectId/{confirm,remove} routes and
// the CALLER (the corpus canvas / cleanup-crew deck) is responsible for
// reconciling the returned state — this component only renders + emits.
//
// PURE-VIEW-FUNCTION TESTING (mirrors AxisLensCard.tsx's own
// deriveAxisSignatureView precedent): deriveAxisChipViews is the pure
// data->view derivation, independently unit-testable with plain arrays, zero
// React/DOM — this monorepo has no jsdom/@testing-library wired into any
// vitest config, so every branch needing coverage is split out as a pure fn.
// =============================================================================

import { useState } from 'react';
import { humanizeAxisKey } from './AxisLensCard';
import styles from './AxisAffinityChips.module.css';

/** Mirrors AxisAffinityTaggerService.ts's AxisAffinity shape verbatim — the
 *  served jsonb element, one per tagged axis on a card/facet. */
export interface AxisAffinityRecord {
  axisKey: string;
  score: number;
  source: 'model' | 'user';
  taxonomyVersion: number;
  taggedAt: string;
  evidence: string;
}

export type AxisAffinitySubjectType = 'achievement' | 'facet';

/** One chip's fully-resolved render state — pure data, no JSX. */
export interface AxisChipView {
  axisKey: string;
  label: string;
  /** Whether the confirm affordance should render (only for source:'model' —
   *  a source:'user' chip is already the user's own terminal ruling, nothing
   *  left to confirm). */
  canConfirm: boolean;
  terminal: boolean;
}

/** Pure: affinities[] -> the exact chip views AxisAffinityChips renders,
 *  sorted by score descending (highest-confidence tag first) then axisKey
 *  (stable, deterministic — mirrors tracedWeights' own ordering discipline). */
export function deriveAxisChipViews(affinities: AxisAffinityRecord[]): AxisChipView[] {
  return [...affinities]
    .sort((a, b) => b.score - a.score || a.axisKey.localeCompare(b.axisKey))
    .map((a) => ({
      axisKey: a.axisKey,
      label: humanizeAxisKey(a.axisKey),
      canConfirm: a.source === 'model',
      terminal: a.source === 'user',
    }));
}

export interface AxisAffinityChipsProps {
  affinities: AxisAffinityRecord[];
  subjectType: AxisAffinitySubjectType;
  /** Called on a confirm tap — the caller owns the API call + optimistic
   *  state reconciliation (served-data-only: this component never mutates
   *  its own copy of the array). Returns the honest error message on
   *  failure, or null on success — same contract as AchievementCard's own
   *  onSaveEdit. Absent -> the confirm affordance never renders (read-only
   *  mode, e.g. a preview surface). */
  onConfirm?: (axisKey: string) => Promise<string | null>;
  /** Called on a remove tap — same contract as onConfirm. Absent -> the
   *  remove affordance never renders. */
  onRemove?: (axisKey: string) => Promise<string | null>;
}

export function AxisAffinityChips({
  affinities,
  subjectType: _subjectType,
  onConfirm,
  onRemove,
}: AxisAffinityChipsProps) {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const views = deriveAxisChipViews(affinities);
  if (views.length === 0) return null;

  async function handle(axisKey: string, action: 'confirm' | 'remove') {
    const handler = action === 'confirm' ? onConfirm : onRemove;
    if (!handler) return;
    setPendingKey(axisKey);
    setErrorKey(null);
    const err = await handler(axisKey);
    setPendingKey(null);
    if (err) setErrorKey(axisKey);
  }

  return (
    <div className={styles.row}>
      {views.map((view) => (
        <span
          key={view.axisKey}
          className={styles.chip}
          data-terminal={view.terminal ? 'true' : undefined}
        >
          <span className={styles.chipLabel}>{view.label}</span>
          {view.canConfirm && onConfirm ? (
            <button
              type="button"
              className={styles.chipAction}
              disabled={pendingKey === view.axisKey}
              aria-label={`Confirm "${view.label}" tag`}
              onClick={() => handle(view.axisKey, 'confirm')}
            >
              ✓
            </button>
          ) : null}
          {onRemove ? (
            <button
              type="button"
              className={styles.chipAction}
              disabled={pendingKey === view.axisKey}
              aria-label={`Remove "${view.label}" tag`}
              onClick={() => handle(view.axisKey, 'remove')}
            >
              ×
            </button>
          ) : null}
          {errorKey === view.axisKey ? <span className={styles.chipError}>failed</span> : null}
        </span>
      ))}
    </div>
  );
}
